/**
 * InvestorBrochureUploadPage — drag-and-drop a firm tearsheet / fund
 * overview PDF, let Claude extract the investor profile, and
 * auto-create / update the Investor record.
 *
 * Mirrors CompanyBrochureUploadPage. One-shot flow in v1 (extract +
 * commit in one request).
 */
import React, { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  CheckCircle2,
  Globe,
  Loader2,
  Mail,
  UploadCloud,
  Users,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import investorBrochureService from '../services/investorBrochureService';
import {
  InvestorBrochureProfile,
  InvestorBrochureUploadSuccess,
} from '../types/investorBrochure';

const MAX_BYTES = 25 * 1024 * 1024;

const formatMoney = (n?: number | null) => {
  if (n == null) return null;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const labelize = (s?: string | null) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null;

const ProfileCard: React.FC<{
  profile: InvestorBrochureProfile;
  summary?: InvestorBrochureUploadSuccess;
}> = ({ profile, summary }) => {
  const loc = [
    profile.location_city,
    profile.location_state,
    profile.location_country,
  ]
    .filter(Boolean)
    .join(', ');

  const checkSize =
    profile.check_size_min_usd != null || profile.check_size_max_usd != null
      ? `${formatMoney(profile.check_size_min_usd) ?? '?'} – ${
          formatMoney(profile.check_size_max_usd) ?? '?'
        }`
      : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {profile.firm_name || profile.full_name || 'Unknown investor'}
        </h2>
        {profile.firm_name && profile.full_name && (
          <p className="text-sm text-gray-600 mt-1">
            {profile.full_name}
            {profile.title ? ` — ${profile.title}` : ''}
          </p>
        )}
        {profile.investor_type && (
          <span className="inline-flex items-center mt-2 px-2.5 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-purple-700 border border-purple-200">
            {labelize(profile.investor_type)}
          </span>
        )}
        {profile.website_url && (
          <a
            href={profile.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 mt-1 flex items-center"
          >
            <Globe className="w-3.5 h-3.5 mr-1" />
            {profile.website_url}
          </a>
        )}
      </div>

      {profile.investment_philosophy && (
        <p className="text-sm text-gray-700 leading-relaxed">
          {profile.investment_philosophy}
        </p>
      )}

      {profile.sector_focus && profile.sector_focus.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {profile.sector_focus.map((s) => (
            <span
              key={s}
              className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200"
            >
              {s}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
        {loc && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              Location
            </p>
            <p className="text-sm text-gray-900">{loc}</p>
          </div>
        )}
        {profile.stage_preferences && profile.stage_preferences.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              Stages
            </p>
            <p className="text-sm text-gray-900">
              {profile.stage_preferences.map(labelize).join(', ')}
            </p>
          </div>
        )}
        {checkSize && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              Check size
            </p>
            <p className="text-sm text-gray-900">{checkSize}</p>
          </div>
        )}
        {profile.fund_size_usd != null && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              Fund size
            </p>
            <p className="text-sm text-gray-900">
              {formatMoney(profile.fund_size_usd)}
            </p>
          </div>
        )}
        {profile.estimated_aum_usd != null && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              Estimated AUM
            </p>
            <p className="text-sm text-gray-900">
              {formatMoney(profile.estimated_aum_usd)}
            </p>
          </div>
        )}
      </div>

      {profile.email && (
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
            Contact
          </p>
          <div className="flex items-center text-sm text-gray-800">
            <Mail className="w-4 h-4 mr-2 text-gray-400" />
            <a
              href={`mailto:${profile.email}`}
              className="text-blue-600 hover:underline"
            >
              {profile.email}
            </a>
          </div>
        </div>
      )}

      {profile.team_members && profile.team_members.length > 0 && (
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center">
            <Users className="w-3.5 h-3.5 mr-1" />
            Team
          </p>
          <ul className="space-y-1">
            {profile.team_members.map((m, idx) => (
              <li key={`${m.name}-${idx}`} className="text-sm text-gray-800">
                <span className="font-medium">{m.name}</span>
                {m.role && <span className="text-gray-500"> — {m.role}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {profile.notable_portfolio && profile.notable_portfolio.length > 0 && (
        <div className="pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
            Notable portfolio
          </p>
          <div className="flex flex-wrap gap-2">
            {profile.notable_portfolio.map((p, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {summary && (
        <div className="pt-4 border-t border-gray-100 bg-green-50 -mx-6 -mb-6 px-6 py-4 rounded-b-xl">
          <div className="flex items-center text-green-800 mb-2">
            <CheckCircle2 className="w-5 h-5 mr-2" />
            <p className="text-sm font-semibold">
              {summary.is_new_investor
                ? `Created new investor #${summary.investor_id}`
                : `Matched existing investor: ${summary.investor_name}`}
            </p>
          </div>
          {summary.fields_updated.length > 0 && (
            <p className="text-xs text-green-700 mb-3">
              Fields populated: {summary.fields_updated.join(', ')}
            </p>
          )}
          <div className="flex gap-2">
            <Link
              to={`/investors/${summary.investor_id}`}
              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            >
              Open investor
            </Link>
            <Link
              to="/investors"
              className="inline-flex items-center px-3 py-1.5 bg-white text-gray-800 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
            >
              All investors
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const InvestorBrochureUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [profile, setProfile] = useState<InvestorBrochureProfile | null>(null);
  const [summary, setSummary] = useState<InvestorBrochureUploadSuccess | null>(
    null
  );
  const [needsReviewReason, setNeedsReviewReason] = useState<string | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!file) return;
    if (
      !file.name.toLowerCase().endsWith('.pdf') &&
      file.type !== 'application/pdf'
    ) {
      toast.error('Please upload a PDF');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (max ${MAX_BYTES / (1024 * 1024)} MB)`);
      return;
    }
    setBusy(true);
    setProfile(null);
    setSummary(null);
    setNeedsReviewReason(null);

    const res = await investorBrochureService.uploadInvestorBrochure(file);
    setBusy(false);
    if (res.error || !res.data) {
      toast.error(res.error || 'Upload failed');
      return;
    }
    if (res.data.status === 'ok') {
      setProfile(res.data.profile);
      setSummary(res.data);
      toast.success(
        res.data.is_new_investor
          ? `Created new investor: ${res.data.investor_name}`
          : `Matched existing investor: ${res.data.investor_name}`
      );
    } else {
      setProfile(res.data.profile);
      setNeedsReviewReason(res.data.reason);
      toast('Extraction needs review — check the profile below', {
        icon: 'ℹ️',
      });
    }
  }, []);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/investors')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to investors
          </button>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Upload investor brochure
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Drop a firm tearsheet, fund overview deck, or partner bio PDF.
            Claude extracts the investor profile and creates or updates the
            record.
          </p>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`bg-white border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.currentTarget.value = '';
            }}
          />
          {busy ? (
            <div className="flex flex-col items-center text-gray-600">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-3" />
              <p className="font-medium">Extracting with Claude Vision…</p>
              <p className="text-xs text-gray-500 mt-1">
                This can take 10-20 seconds for image-heavy decks.
              </p>
            </div>
          ) : (
            <>
              <UploadCloud className="w-10 h-10 mx-auto text-blue-600 mb-3" />
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Drop a PDF here, or click to select
              </h2>
              <p className="text-sm text-gray-600">
                Firm tearsheets, fund overviews, partner bios. Max 25 MB.
              </p>
            </>
          )}
        </div>

        {needsReviewReason && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            <p className="font-semibold mb-1">Needs review</p>
            <p>{needsReviewReason}</p>
            <p className="mt-2 text-xs">
              The extracted profile is shown below, but nothing was saved yet.
              Upload a different PDF or add the investor manually from the
              Investors page.
            </p>
          </div>
        )}

        {profile && (
          <ProfileCard profile={profile} summary={summary || undefined} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default InvestorBrochureUploadPage;
